import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material'
import { GreenText } from 'components'
import { MichiganTitleVehiclePoliceBookDto } from 'dtos'
import { useFormikContext } from 'formik'
import { useEffect, useState } from 'react'
import {
  michiganTitlesCompactButtonSx,
  michiganTitlesCompactCellSx,
  michiganTitlesNextPoliceFieldSx,
  michiganTitlesPoliceBookNumberFieldSx,
  michiganTitlesPoliceBookToolbarSx,
  michiganTitlesTableContainerSx,
  michiganTitlesTableSx
} from './michiganTitlesTableStyles'

export default function PoliceHBookCard() {
  const { errors, handleBlur, handleChange, handleSubmit, isSubmitting, setFieldValue, touched, values } =
    useFormikContext<MichiganTitleVehiclePoliceBookDto[]>()

  const [nextPoliceNumber, setNextPoliceNumber] = useState<string>('')
  const [selectedVehicleIndexes, setSelectedVehicleIndexes] = useState<number[]>([])

  useEffect(() => {
    setSelectedVehicleIndexes([])
  }, [values.length])

  return (
    <Card>
      <CardContent>
        <Grid container spacing={1} alignItems='center'>
          <Grid item xs={12}>
            <Typography fontWeight='bold' variant='h6'>
              Police Book
            </Typography>
          </Grid>

          <Grid item xs={12}>
            <Box sx={michiganTitlesPoliceBookToolbarSx}>
              <TextField
                disabled={isSubmitting}
                onChange={e => {
                  setNextPoliceNumber(e.target.value)
                }}
                placeholder='Next Police #'
                size='small'
                sx={michiganTitlesNextPoliceFieldSx}
                type='number'
                value={nextPoliceNumber}
              />

              <Button
                color='primary'
                disabled={
                  isSubmitting || !nextPoliceNumber || isNaN(parseInt(nextPoliceNumber)) || selectedVehicleIndexes.length === 0
                }
                onClick={() => {
                  const start = parseInt(nextPoliceNumber)

                  for (let i = 0; i < selectedVehicleIndexes.length; i++) {
                    setFieldValue(`[${selectedVehicleIndexes[i]}].michiganTitlePoliceBookNumber`, start + i)
                  }
                }}
                size='small'
                sx={michiganTitlesCompactButtonSx}
                variant='contained'
              >
                APPLY
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <TableContainer sx={michiganTitlesTableContainerSx}>
              <Table size='small' sx={michiganTitlesTableSx}>
                <TableHead>
                  <TableRow>
                    <TableCell padding='checkbox'></TableCell>

                    <TableCell>Importer Stock #</TableCell>

                    <TableCell>PAPS #</TableCell>

                    <TableCell>Police Book #</TableCell>

                    <TableCell>Signed BOL</TableCell>

                    <TableCell>VIN</TableCell>

                    <TableCell>Year</TableCell>

                    <TableCell>Make</TableCell>

                    <TableCell>Model</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {values.map((vehicle, index) => (
                    <TableRow key={vehicle.vehicleGuid} sx={{ backgroundColor: index % 2 === 0 ? '#FCFCFD' : 'white' }}>
                      <TableCell padding='checkbox'>
                        <Checkbox
                          checked={selectedVehicleIndexes.includes(index)}
                          disabled={isSubmitting}
                          size='small'
                          onChange={() => {
                            setSelectedVehicleIndexes(
                              (selectedVehicleIndexes.includes(index)
                                ? selectedVehicleIndexes.filter(i => i !== index)
                                : [...selectedVehicleIndexes, index]
                              ).sort((a, b) => a - b)
                            )
                          }}
                        />
                      </TableCell>

                      <TableCell sx={michiganTitlesCompactCellSx}>{vehicle.importerStockNumber}</TableCell>

                      <TableCell sx={michiganTitlesCompactCellSx}>{vehicle.papsNumber}</TableCell>

                      <TableCell>
                        <TextField
                          disabled={isSubmitting}
                          error={Boolean(
                            touched[index]?.michiganTitlePoliceBookNumber && errors[index]?.michiganTitlePoliceBookNumber
                          )}
                          helperText={
                            touched[index]?.michiganTitlePoliceBookNumber && errors[index]?.michiganTitlePoliceBookNumber
                          }
                          margin='dense'
                          name={`[${index}].michiganTitlePoliceBookNumber`}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          size='small'
                          sx={michiganTitlesPoliceBookNumberFieldSx}
                          type='number'
                          value={values[index].michiganTitlePoliceBookNumber}
                        />
                      </TableCell>

                      <TableCell>-</TableCell>

                      <TableCell sx={michiganTitlesCompactCellSx}>{vehicle.vin}</TableCell>

                      <TableCell sx={michiganTitlesCompactCellSx}>{vehicle.year}</TableCell>

                      <TableCell>{vehicle.make}</TableCell>

                      <TableCell>{vehicle.model}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12} container spacing={1} alignItems='center' justifyContent='flex-end'>
            <Grid item>
              <Button
                color='primary'
                disabled={isSubmitting}
                size='small'
                sx={michiganTitlesCompactButtonSx}
                type='submit'
                variant='contained'
              >
                SAVE
              </Button>
            </Grid>

            <Grid item>
              <Button
                color='primary'
                disabled={isSubmitting || selectedVehicleIndexes.length === 0}
                onClick={() => {
                  if (
                    window.confirm(
                      'Are you sure you wish to mark this application packet as Ready to Print? This cannot be undone.'
                    )
                  ) {
                    for (let i = 0; i < selectedVehicleIndexes.length; i++) {
                      setFieldValue(`[${selectedVehicleIndexes[i]}].michiganTitleIsApplicationPacketReadyToPrint`, true)
                    }

                    handleSubmit()
                  }
                }}
                size='small'
                sx={michiganTitlesCompactButtonSx}
                variant='contained'
              >
                SAVE AND MARK APPLICATION PACKET READY TO PRINT
              </Button>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>{`**Next Police Book Field/Apply Button:**

Within the Police Book table, users can enter a value into the “Next Police Book #” field and press “Apply”. When applied, VTS2 will automatically populate the entered number and subsequent sequential numbers into the “Police Book #” field for each selected vehicle.

Pressing “SAVE” will save the assigned Police Book # values to the Vehicle Details – Titles (tab) on the Michigan Title Application Packet card.

Note: There is no unique value check on the Police Book # field.

**Police Book Table:**

The Police Book table displays all selected vehicles. For the “Save And Mark Application Packet Ready To Print” button to be enabled, each selected vehicle must have a value in its respective “Police Book #” field.

Pressing the “Save And Mark Application Packet Ready To Print” button will display a confirmation pop-up:
   - “Are you sure you wish to mark this application packet as Ready to Print? This cannot be undone.”
   - If the user confirms by selecting “Yes”, the selected vehicles will be moved into the “Application Ready to Print” table.
   - The “Application Packet Ready to Print” column for those vehicles will be updated accordingly.

**SAVE Button:**

Saves all entered Police Book # values to the Vehicle Details – Titles (tab) of the Michigan Title Application Packet card.

This action does not deselect the vehicles or move them out of the Police Book table, allowing users to continue editing as needed.

**SAVE AND MARK APPLICATION PACKET READY TO PRINT Button:**
All selected vehicles must have a value in their “Police Book #” field to enable this button.

When pressed, a confirmation pop-up appears, asking:
   - “Are you sure you wish to mark this application packet as Ready to Print? This cannot be undone.”

If confirmed, the selected vehicles are moved into the “Application Ready to Print” table.

The “Application Packet Ready to Print” column is updated for those vehicles to reflect their status.`}</GreenText>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  )
}
